export const landing = {
  extends: 'Page',
  flow: 'y',
  background: 'surface',

  LandingNav: {},

  // ═══════════════════════════════════════════════════
  // HERO — Good Fella style: big text left, ASCII art right
  // ═══════════════════════════════════════════════════
  HeroSection: {
    tag: 'section',
    flow: 'x',
    align: 'flex-end',
    gap: 'C',
    padding: 'F D E D',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    maxWidth: 'K',
    margin: '0 auto',
    width: '100%',
    '@tabletS': { padding: 'E B D B' },
    '@mobileL': { flow: 'y', padding: 'D A C A', gap: 'B' },

    HeroLeft: {
      flow: 'y',
      gap: 'B',
      flex: '1',
      zIndex: '1',

      HeroTitle: {
        tag: 'h1',
        flow: 'y',
        gap: '0',
        TitleLine1: {
          text: '{{ heroLine1 | polyglot }}',
          fontSize: 'C',
          fontWeight: '300',
          color: 'text',
          lineHeight: '1.15',
          letterSpacing: '-Z',
          '@mobileL': { fontSize: 'B' }
        },
        TitleLine2: {
          text: '{{ heroLine2 | polyglot }}',
          fontSize: 'C',
          fontWeight: '300',
          color: 'text',
          lineHeight: '1.15',
          letterSpacing: '-Z',
          '@mobileL': { fontSize: 'B' }
        },
        TitleLine3: {
          text: '{{ heroLine3 | polyglot }}',
          fontSize: 'C',
          fontWeight: '700',
          color: 'green',
          lineHeight: '1.15',
          letterSpacing: '-Z',
          '@mobileL': { fontSize: 'B' }
        }
      },

      HeroDesc: {
        text: '{{ heroSubtitle | polyglot }}',
        fontSize: 'A',
        color: 'textMuted',
        lineHeight: '1.6',
        maxWidth: 'H'
      },

      HeroCtas: {
        flow: 'x',
        gap: 'A',
        align: 'center',
        '@mobileL': { flow: 'y', width: '100%' },
        TrialLink: {
          extends: 'Link',
          href: '/login',
          text: '{{ tryFree | polyglot }}',
          background: 'green',
          color: 'neutral950',
          padding: 'Z B',
          fontSize: 'Z',
          fontWeight: '600',
          borderRadius: 'B',
          textDecoration: 'none',
          transition: 'transform 200ms',
          ':hover': { transform: 'scale(0.98)' },
          '@mobileL': { width: '100%', textAlign: 'center' }
        },
        DemoLink: {
          extends: 'Link',
          href: '/dashboard',
          text: '{{ liveDemo | polyglot }}',
          background: 'transparent',
          color: 'text',
          border: '1px solid surfaceBorderStrong',
          padding: 'Z B',
          fontSize: 'Z',
          fontWeight: '500',
          borderRadius: 'B',
          textDecoration: 'none',
          transition: 'transform 200ms',
          ':hover': { transform: 'scale(0.98)' },
          onClick: (e, el, s) => s.root.update({ loggedIn: true }),
          '@mobileL': { width: '100%', textAlign: 'center' }
        }
      }
    },

    // Trust bar — pinned to bottom of hero, visible above the fold
    TrustBar: {
      tag: 'section',
      position: 'absolute',
      bottom: '0',
      left: '0',
      right: '0',
      flow: 'x',
      gap: 'B',
      padding: 'A D',
      align: 'center',
      background: 'black .75',
      backdropFilter: 'blur(8px)',
      borderTop: '1px solid surfaceBorderStrong',
      zIndex: '2',
      '@mobileL': { flow: 'y', padding: 'A', gap: 'Z' },

      TrustLabel: {
        text: '{{ trustedBrands | polyglot }}',
        fontSize: 'Y',
        color: 'textMuted',
        textTransform: 'uppercase',
        letterSpacing: 'X',
        flex: '1',
        '@mobileL': { textAlign: 'center' }
      },
      TrustLogos: {
        flow: 'x',
        gap: 'C',
        align: 'center',
        opacity: '.5',
        '@mobileL': { gap: 'A', flexWrap: 'wrap', justifyContent: 'center' },
        Logo1: { text: 'ბარათელი', fontSize: 'A', fontWeight: '700', color: 'text' },
        Logo2: { text: 'LegalHub', fontSize: 'A', fontWeight: '700', color: 'text' },
        Logo3: { text: 'JustiCo', fontSize: 'A', fontWeight: '700', color: 'text' }
      }
    },

    // ASCII Lady Justice
    HeroAscii: {
      position: 'absolute',
      top: '0',
      right: '0',
      bottom: '0',
      width: '65%',
      overflow: 'visible',
      pointerEvents: 'none',
      '@mobileL': { display: 'none' },

      AsciiArt: {
        tag: 'pre',
        position: 'absolute',
        top: 'C',
        right: '0',
        overflow: 'visible',
        fontFamily: 'monospace',
        fontSize: 'B',
        lineHeight: '1.2',
        color: 'green',
        whiteSpace: 'pre',
        textAlign: 'left',
        text: '                                                                             .:                            \n                                                                            .*#:                           \n                                                                             #%.                           \n                                                                           :=*%:                           \n                                                                           *%%%%.                          \n                                                                           *#%%%-                          \n                                                                            -*@@:                          \n                                                      .::                   -*@%                           \n                                                  +*+*##*#%*                **%=                           \n                                                .**%*#%#%%%@%=            :**#%                            \n                                                *#%**%%%%##%@:           -#=*%-                            \n                                                :%*%%*=+****#           -%=*#%-                            \n                                                 =*#%+==%%%##          :%#*#*%=                            \n                                                 :*%%==#%#%%*        :*+=*%-#%%                            \n                                                :#@@*%**%%%%       .*+%%%* :%+:                            \n                                               .*%*@*-*@@%=:     :*+#@%%=%@%-:%-=-                         \n                                               +#%#@#=%*%:     :**#%%%=--..*%@@%@*@*-#                     \n                                              =*%@#%=:*-%#.  =%#*%*##*-     -:   =+:*=                     \n                                             :*%#++#+-##%%***#***#*::+:             +:                     \n                                          :*=+*+=*+==++*++=+*+*%%=  .=-             ==                     \n                                         :-:#*#*==**+++==+++++*%+   :=-.            -=                     \n                                        :=:%%%+====+:--**+=--=%*    :=::            :::                    \n                                        ==%%@@@*=-. #%##**#*%+=%    :-.=           ::.:                    \n                                       -=*+#--@***-*%#+#*#**=###   ..:.-           ::.:                    \n                                      ::*=*= :@*#**+*##*#**+*#%=   - :..:          :.::                    \n                                     -:+=*-  :*++****++#**=+**#:   = :: :          :.::.                   \n                                    ::+*#=    #@#**=%%%#**=*##     : :: :         ...:.:                   \n                                   .:+#%-     .*%*+*##%%###*#*    .: :: -         . .: =                   \n                                  :=**%:    ==%%%*+**#%***#%%     .. .: :.        : .: -                   \n                                 +=*#*:    :#*#%*=+=-++***%%%.    :  .:  :        - .: ..                  \n                                =***#=     :**#*+=*##+*=+#%#*=    =  .:  =        :  :  :                  \n                               -=*-*=      =*+*==-*#%%==*#%*%=    =   :  .       :.  :  :                  \n                              :=*=*:       =-*-:++=#%%=.=#**#-   ::   :  ..      -   -  :                  \n                             :-+-*.       =*+::--*+*#%*:+#*%#=   -    :   :      :   -  ..                 \n                             -+=+         :*:..=*#***#*+*****=   :    :   -      :   -   :                 \n                       :-=  -=::          :*:..*###%*%*#%#%#*=  .-::====+***:   :++**####%*                \n                        -=:::==           +*=..*#+*%%%%%*%#*#=   .*%%%%%%%*       -%%%%%+.                 \n                         :+:#*            **-::##:+*%%%++#****:      .:.                                   \n                         :==**=           **-=*%#.-*##*=***+##*                                            \n                          =*#*= .==       *#+**%#:+*%*+=**+#*#=                                            \n                           :==+++:        *+=***#=**%==***+=**%.                                           \n                          :-+**=+.        *#=*****#%*++*#=***+%*                                           \n                          .   =+#*:       %%+*****#@*=%*=:+***%*                                           \n                               :+**:     =@%=*##**%@**##:*+*+*%%:                                          \n                                :***-   =%@%****#*%@#%%#==#%*=*%+                                          \n                                 :**#- =*@@%#*=**#%@%%@%*#@%#=*#:                                          \n                                  :*#****@@@%%*:+*%@%%@@%%*%%===                                           \n                                   :*****%@@%%%*-**@@%@@%%+*%===                                           \n                                    :##***@@%%%%++*#@%@%%@%=%+:=                                           \n                                   .+%##**@@%#%%*+*%%%%%%@@%%#+:                                           \n                                    +**##*#@%*%@#**%%#%@@@@#**%.                                           \n                                     =**##**%#%@@*%@%*%@@@@@@%:                                            \n                                      .*%##**%@@@#*%@**@@@%@@+                                             \n                                       -*###*++%@@*%@%*%@%@@#                                              \n                                     .==+##%%++%@%%#@%*%@%@%.                                              \n                                    :=-:-+*%##++@##%@**%@@%:                                               \n                                    .-=--=*##%%+=%%@%**%@@-                                                \n                                    :==-=+%#=%#%+=%%%##@@#                                                 \n                                     *=-+#@%+*%##==%*%%@%*                                                 \n                                     +==*%@@*=%#%#+=*#%@@%+                                                \n                                     =-+*#@@%=##@%#+=*%@@@**.                                              \n                                     -+#*#@@@+*%%%%#+-*%@@@%@*                                             \n                                     =%%%##%@#+%%%@%#+-#%%@%@@%.                                           \n                                      *%%***@%+%%@@@%%*-%@@@@@@**+*++-                                     \n                                       --=*##@%%#@@@@%#+:%@@@@@@@@@@%%                                     \n                                         :*%%@%%%%@@@@%%+-*%@@@@@@@%%#                                     \n                                          .*@@@*-*%@@@@%%+-+*%@@%%@@%=                                     \n                                       +%%%%@@@%=-+*%@%@%%+-%@%#*#%%@%+                                    \n                                   =*=*#@@@%%%%@%***#%%%%@@++%@@%@%%%%%%*=.                                \n                                   -*#%%%%##%%%%%%%@%%%#%@%@*%%%%%%%%%%%%##%%.                             \n                                  *%%%%%%%%%%%%%%#%%%%#%##%%%%%%%%%%%%%%%%%%@*+=                           \n                              *###%%%%%%%%%%%%%%%%%%##**%%%%%%%%%%%%%%%%#####%%%:                          \n                              *#####%###############****%%%####*****##########%%:                          \n                              +###%#%%#####*###########*###******#########%%%%%%:                          \n                              =##########******###**#***#####**#%#########%##%%%-                          \n                               :-==+*****************####%%%%########***=-::.                              \n                                            .:::-==++**##%#*+=-:.                                          ',

        onRender: (el) => {
          if (el.__animInit) return
          el.__animInit = true
          if (!el.node) return
          setTimeout(function () {
            var txt = el.node.textContent
            el.node.textContent = ''
            var spans = []
            var row = 0
            var col = 0
            for (var i = 0; i < txt.length; i++) {
              var c = txt[i]
              if (c === '\n') {
                el.node.appendChild(document.createTextNode('\n'))
                row++
                col = 0
              } else if (c !== ' ') {
                var s = document.createElement('span')
                s.textContent = c
                s.style.display = 'inline-block'
                s.style.width = '1ch'
                s.style.textAlign = 'center'
                s._row = row
                s._col = col
                el.node.appendChild(s)
                spans.push(s)
                col++
              } else {
                var sp = document.createElement('span')
                sp.textContent = ' '
                sp.style.display = 'inline-block'
                sp.style.width = '1ch'
                el.node.appendChild(sp)
                col++
              }
            }
            var mouseX = -1000
            var mouseY = -1000
            var preRect = null
            setTimeout(function () {
              var hero = el.node.closest('section') || document.body
              hero.addEventListener('mousemove', function (e) {
                preRect = el.node.getBoundingClientRect()
                mouseX = e.clientX
                mouseY = e.clientY
              })
              hero.addEventListener('mouseleave', function () {
                mouseX = -1000
                mouseY = -1000
              })
            }, 100)

            for (var k = 0; k < spans.length; k++) {
              spans[k]._tx = 0
              spans[k]._ty = 0
              spans[k].style.transition = 'transform 0.4s ease-out'
            }

            var draw = function (time) {
              var t = time * 0.0008
              if (!preRect) preRect = el.node.getBoundingClientRect()
              for (var j = 0; j < spans.length; j++) {
                var s = spans[j]
                var w1 = Math.sin(s._row * 0.15 + t * 1.8 + s._col * 0.03)
                var w2 = Math.cos(s._col * 0.1 + t * 1.3 + s._row * 0.05)
                var w3 = Math.sin((s._row + s._col) * 0.08 + t * 0.7)
                var mix = (w1 + w2 + w3) / 3
                var brightness = 0.15 + (mix * 0.5 + 0.5) * 0.7
                var hueShift = Math.sin(t * 0.5 + s._row * 0.04) * 0.3
                var r = Math.floor((198 + hueShift * 60) * brightness)
                var g = Math.floor((172 - hueShift * 20) * brightness)
                var b = Math.floor((143 - hueShift * 40) * brightness)
                r = Math.min(255, Math.max(0, r))
                g = Math.min(255, Math.max(0, g))
                b = Math.min(255, Math.max(0, b))
                s.style.color = 'rgb(' + r + ',' + g + ',' + b + ')'

                var sx = preRect.left + s._col * (s.offsetWidth || 10)
                var sy = preRect.top + s._row * (s.offsetHeight || 20)
                var dx = sx - mouseX
                var dy = sy - mouseY
                var dist = Math.sqrt(dx * dx + dy * dy)
                var radius = 150
                if (dist < radius && mouseX > -999) {
                  var force = 1 - dist / radius
                  var hr = Math.floor(255 * force + r * (1 - force))
                  var hg = Math.floor(230 * force + g * (1 - force))
                  var hb = Math.floor(200 * force + b * (1 - force))
                  s.style.color = 'rgb(' + hr + ',' + hg + ',' + hb + ')'
                }
              }
              requestAnimationFrame(draw)
            }
            requestAnimationFrame(draw)
          }, 200)
        }
      }
    }
  },

  // Lawyer search bar
  LawyerSearch: {
    tag: 'section',
    flow: 'y',
    align: 'center center',
    gap: 'X2',
    padding: 'D D',
    background: 'surfaceMid',
    borderTop: '1px solid surfaceBorderStrong',
    borderBottom: '1px solid surfaceBorderStrong',
    '@mobileL': { padding: 'B A' },
    maxWidth: 'K',
    width: '100%',
    margin: '0 auto',

    SearchTitle: {
      text: '{{ findLawyer | polyglot }}',
      fontSize: 'C',
      fontWeight: '700',
      color: 'text'
    },
    SearchSub: {
      text: (el, s) => {
        const l = s.root.totalLawyers || (s.root.lawyers || []).length
        const f = s.root.totalFirms || (s.root.firms || []).length
        const lStr = l > 0 ? l.toLocaleString() + '+' : '5,700+'
        const fStr = f > 0 ? f.toLocaleString() + '+' : '300+'
        return lStr + ' Lawyers · ' + fStr + ' Firms registered in Georgia'
      },
      fontSize: 'A',
      color: 'textMuted'
    },

    SearchRow: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      width: '100%',
      maxWidth: 'J',
      margin: 'A -',
      '@mobileL': { flow: 'y' },
      SearchInput: {
        tag: 'input',
        type: 'text',
        placeholder: '{{ searchPlaceholder | polyglot }}',
        flex: '1',
        padding: 'Z A',
        fontSize: 'A',
        borderRadius: 'Z',
        background: 'surfaceCard',
        border: '1px solid surfaceBorderStrong',
        color: 'text',
        outline: 'none',
        onInput: (e, el, s) => s.root.update({ directoryFilter: el.node.value }),
        onKeyDown: (e, el, s) => {
          if (e.key === 'Enter') {
            const q = el.node.value.trim()
            s.root.update({ directoryFilter: q })
            window.location.href = q ? '/lawyers?q=' + encodeURIComponent(q) : '/lawyers'
          }
        }
      },
      SearchBtn: {
        tag: 'button',
        text: '{{ searchBtn | polyglot }}',
        background: 'green',
        color: 'neutral950',
        border: 'none',
        fontWeight: '600',
        padding: 'Z B',
        fontSize: 'A',
        borderRadius: 'Z',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        onClick: (e, el, s) => {
          const input = el.parent?.SearchInput?.node
          const q = (input ? input.value : s.root.directoryFilter || '').trim()
          s.root.update({ directoryFilter: q })
          window.location.href = q ? '/lawyers?q=' + encodeURIComponent(q) : '/lawyers'
        }
      }
    },

    QuickFilters: {
      flow: 'x',
      gap: 'Y',
      flexWrap: 'wrap',
      align: 'center center',
      childExtends: 'Link',
      childProps: {
        textDecoration: 'none',
        padding: 'Y Z',
        fontSize: 'Z',
        borderRadius: 'Z',
        background: 'transparent',
        color: 'textMuted',
        border: '1px solid surfaceBorderStrong',
        ':hover': { borderColor: 'green', color: 'green' }
      },
      F1: { href: '/lawyers?area=Corporate+Law', text: 'Corporate Law' },
      F2: { href: '/lawyers?area=Criminal+Law', text: 'Criminal Law' },
      F3: { href: '/lawyers?area=Family+Law', text: 'Family Law' },
      F4: { href: '/lawyers?area=Real+Estate', text: 'Real Estate' },
      F5: { href: '/lawyers?area=Immigration', text: 'Immigration' },
      F6: { href: '/lawyers', text: '{{ viewAll | polyglot }} →' }
    },

    // ── Preview listings with gradient fade ──────────────
    PreviewWrap: {
      position: 'relative',
      width: '100%',
      maxWidth: 'K',
      margin: 'B auto 0',
      overflow: 'hidden',

      PreviewGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        '@tabletS': { gridTemplateColumns: 'repeat(2, 1fr)' },
        '@mobileL': { gridTemplateColumns: '1fr' },
        gap: 'B',
        pointerEvents: 'none',
        children: [
          { nameEn: 'Giorgi Beridze', nameKa: 'გიორგი ბერიძე', firmNameEn: 'Beridze & Partners', city: 'Tbilisi', specializations: ['Corporate Law', 'M&A'], rating: 4.9, reviews: 87, status: 'active', isSubscribed: true, barId: 'GBA-1042' },
          { nameEn: 'Nino Tsiklauri', nameKa: 'ნინო წიკლაური', firmNameEn: 'Tsiklauri Law Group', city: 'Tbilisi', specializations: ['Family Law', 'Civil Law'], rating: 4.7, reviews: 61, status: 'active', isSubscribed: false, barId: 'GBA-2318' },
          { nameEn: 'David Kvaratskhelia', nameKa: 'დავით კვარაცხელია', firmNameEn: null, city: 'Kutaisi', specializations: ['Criminal Law', 'Human Rights'], rating: 4.8, reviews: 54, status: 'active', isSubscribed: true, barId: 'GBA-0891' },
          { nameEn: 'Tamar Jikia', nameKa: 'თამარ ჯიქია', firmNameEn: 'Jikia Legal', city: 'Tbilisi', specializations: ['Real Estate', 'Tax Law'], rating: 4.6, reviews: 43, status: 'active', isSubscribed: false, barId: 'GBA-3104' },
          { nameEn: 'Levan Chikovanи', nameKa: 'ლევან ჩიქოვანი', firmNameEn: 'Chikovani & Associates', city: 'Batumi', specializations: ['Immigration', 'Administrative Law'], rating: 4.5, reviews: 38, status: 'active', isSubscribed: false, barId: 'GBA-1775' },
          { nameEn: 'Ana Maisuradze', nameKa: 'ანა მაისურაძე', firmNameEn: 'Maisuradze Law Firm', city: 'Tbilisi', specializations: ['IP Law', 'Corporate Law'], rating: 4.9, reviews: 102, status: 'active', isSubscribed: true, barId: 'GBA-0567' },
          { nameEn: 'Irakli Lomidze', nameKa: 'ირაკლი ლომიძე', firmNameEn: null, city: 'Tbilisi', specializations: ['Banking & Finance', 'M&A'], rating: 4.7, reviews: 49, status: 'active', isSubscribed: false, barId: 'GBA-2240' },
          { nameEn: 'Khatia Gambashidze', nameKa: 'ხათია გამბაშიძე', firmNameEn: 'GambaLex', city: 'Rustavi', specializations: ['Labor Law', 'Employment'], rating: 4.6, reviews: 31, status: 'active', isSubscribed: false, barId: 'GBA-3891' },
          { nameEn: 'Zurab Melikidze', nameKa: 'ზურაბ მელიქიძე', firmNameEn: 'Melikidze & Sons', city: 'Tbilisi', specializations: ['Criminal Law', 'Constitutional Law'], rating: 4.8, reviews: 76, status: 'active', isSubscribed: true, barId: 'GBA-0123' },
          { nameEn: 'Salome Kirtadze', nameKa: 'სალომე კირთაძე', firmNameEn: null, city: 'Gori', specializations: ['Civil Law', 'Real Estate'], rating: 4.4, reviews: 28, status: 'active', isSubscribed: false, barId: 'GBA-4412' },
          { nameEn: 'Mikheil Aptsiauri', nameKa: 'მიხეილ აფციაური', firmNameEn: 'Aptsiauri Legal Group', city: 'Tbilisi', specializations: ['Tax Law', 'Banking & Finance'], rating: 4.7, reviews: 55, status: 'active', isSubscribed: false, barId: 'GBA-1980' },
          { nameEn: 'Eka Surmanidze', nameKa: 'ეკა სურმანიძე', firmNameEn: 'Surmanidze Partners', city: 'Batumi', specializations: ['Family Law', 'Juvenile Justice'], rating: 4.8, reviews: 67, status: 'active', isSubscribed: true, barId: 'GBA-2755' }
        ].map(l => ({ ...l, _type: 'lawyer' })),
        childrenAs: 'state',
        childExtends: 'DirectoryCard'
      },

      PreviewFade: {
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        height: 'E1',
        background: 'linear-gradient(to bottom, transparent 0%, rgba(22,26,29,0.75) 50%, rgba(22,26,29,1) 100%)',
        pointerEvents: 'none'
      },

      PreviewCta: {
        position: 'absolute',
        bottom: 'B',
        left: '0',
        right: '0',
        flow: 'y',
        align: 'center center',
        gap: 'Z',
        CtaBtn: {
          extends: 'Link',
          href: '/lawyers',
          text: 'View Full Directory →',
          background: 'green',
          color: 'neutral950',
          fontWeight: '700',
          padding: 'Z C',
          fontSize: 'A',
          borderRadius: 'D',
          textDecoration: 'none',
          transition: 'transform 200ms',
          ':hover': { transform: 'scale(0.98)' }
        },
        CtaText: {
          text: 'Browse all 5,700+ verified lawyers',
          fontSize: 'A',
          color: 'textMuted'
        }
      }
    }
  },


  // ═══════════════════════════════════════════════════
  // CTA
  // ═══════════════════════════════════════════════════
  CtaBanner: {
    tag: 'section',
    flow: 'y',
    align: 'center center',
    padding: 'F D',
    gap: 'B',
    textAlign: 'center',
    maxWidth: 'K',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'D A' },

    CtaTitle: {
      tag: 'h2',
      text: 'Are you a lawyer?',
      fontSize: 'D',
      fontWeight: '700',
      color: 'text',
      '@mobileL': { fontSize: 'C' }
    },
    CtaSub: {
      text: 'Join 5,700+ verified lawyers on TrustLegal. First 30 days free.',
      fontSize: 'A',
      color: 'textMuted',
      maxWidth: 'H'
    },
    CtaCtas: {
      flow: 'x',
      gap: 'A',
      align: 'center',
      CtaBtn: {
        extends: 'Link',
        href: '/login',
        text: 'Try for Free →',
        background: 'green',
        color: 'neutral950',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '600',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' }
      },
      DemoBtn: {
        extends: 'Link',
        href: '/dashboard',
        text: 'See Demo',
        background: 'transparent',
        color: 'text',
        border: '1px solid surfaceBorderStrong',
        padding: 'Z B',
        fontSize: 'Z',
        fontWeight: '500',
        borderRadius: 'B',
        textDecoration: 'none',
        transition: 'transform 200ms',
        ':hover': { transform: 'scale(0.98)' },
        onClick: (e, el, s) => s.root.update({ loggedIn: true })
      }
    }
  },

  // ═══════════════════════════════════════════════════
  // FOOTER
  // ═══════════════════════════════════════════════════
  FooterSection: {
    tag: 'footer',
    flow: 'y',
    gap: 'C',
    padding: 'D 0',
    borderTop: '1px solid surfaceBorderStrong',
    maxWidth: 'K',
    margin: '0 auto',
    width: '100%',
    '@mobileL': { padding: 'C A' },

    FooterTop: {
      flow: 'x',
      gap: 'C',
      '@mobileL': { flow: 'y', gap: 'B' },
      FooterBrand: {
        flow: 'y',
        gap: 'A',
        flex: '1.5',
        minWidth: 'F',
        FooterLogo: {
          extends: 'Link',
          href: '/',
          text: 'TrustLegal',
          fontSize: 'B',
          fontWeight: '800',
          color: 'green',
          textDecoration: 'none'
        },
        FooterTagline: {
          text: '{{ footerTagline | polyglot }}',
          fontSize: 'Z',
          color: 'textMuted',
          lineHeight: '1.6'
        }
      },
      FooterLinks: {
        flow: 'x',
        gap: 'C',
        flex: '2',
        '@mobileL': { flow: 'y', gap: 'B' },
        FooterCol1: {
          flow: 'y',
          gap: 'Z',
          flex: '1',
          ColTitle: {
            text: '{{ productLabel | polyglot }}',
            fontSize: 'Y',
            fontWeight: '600',
            color: 'textMuted',
            textTransform: 'uppercase',
            letterSpacing: 'X',
            padding: '0 0 Z 0'
          },
          ColLink1: {
            extends: 'Link',
            href: '/bookings',
            text: '{{ bookingsLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          },
          ColLink2: {
            extends: 'Link',
            href: '/cases',
            text: '{{ casesLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          },
          ColLink3: {
            extends: 'Link',
            href: '/documents',
            text: '{{ aiDocsLink | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' },
            onClick: (e, el, s) => s.root.update({ loggedIn: true })
          }
        },
        FooterCol2: {
          flow: 'y',
          gap: 'Z',
          flex: '1',
          ColTitle: {
            text: '{{ companyLabel | polyglot }}',
            fontSize: 'Y',
            fontWeight: '600',
            color: 'textMuted',
            textTransform: 'uppercase',
            letterSpacing: 'X',
            padding: '0 0 Z 0'
          },
          ColLink1: {
            extends: 'Link',
            href: '/',
            text: '{{ aboutUs | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' }
          },
          ColLink2: {
            extends: 'Link',
            href: '/',
            text: '{{ careers | polyglot }}',
            fontSize: 'Z',
            color: 'textMuted',
            textDecoration: 'none',
            ':hover': { color: 'text' }
          }
        }
      }
    },
    FooterBottom: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A 0 0 0',
      borderTop: '1px solid surfaceBorderStrong',
      '@mobileL': { flow: 'y', gap: 'Z', align: 'center center' },
      CopyText: {
        text: '{{ copyrightText | polyglot }}',
        fontSize: 'Z',
        color: 'textMuted'
      },
      BuiltWith: {
        text: '{{ builtWith | polyglot }}',
        fontSize: 'Z',
        color: 'textMuted'
      }
    }
  }
}
